import { z } from 'zod/v4';

import { QuantityRuntype } from '../backendTypes';
import { getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { HttpEndpoint } from '../http/HttpEndpoint';
import { AssemblyIdRuntype, PcbIdRuntype } from '../idTypes';
import { CustomOptionPartDTORuntype, PartDTORuntype, StandardPartDTORuntype } from '../part';
import {
    AllOriginRuntype,
    CustomOptionOfferResponseRuntype,
    OfferResponseRuntype,
    StandardPartOfferResponseRuntype,
} from './offerBackendTypes';
import {
    BulkCustomPartOfferRequestDTORuntype,
    CustomPartOfferInputDTORuntype,
    CustomPartOfferUpdateDTORuntype,
    CustomPartOfferWithSolutionsDTO,
    ImportOffersRequestBodyRuntype,
    OfferRefRuntype,
    PartOfferUpdateStatusRuntype,
    PdfAnalyzeResponseRuntype,
    StandardPartOfferInputDTORuntype,
    StandardPartOfferUpdateDTORuntype,
    StandardPartOfferWithSolutionsDTO,
    StandardPartOfferWithSolutionsDTORuntype,
} from './offerBackendTypesOld';
import { FindOffersRequestRuntype, FindOffersResultRuntype } from './offerV2BackendTypes';

// Whenever a change is made to the offer endpoints, the following endpoints should be invalidated.
export const generalOfferEndpointsToInvalidate: HttpEndpoint[] = [
    // Offers
    'GET /offers/custom-part',
    'POST /offers/off-the-shelf/with-solutions',
    'POST /offers/custom-part/with-solutions',
    'POST /offers/find',
    'POST /offers/v1/find',
    'POST /offers/bulk',
    // Sourcing scenarios / Solutions
    'POST /sourcing-scenarios/bulk',
    'GET /sourcing-line-item/:id/solutions',
    'POST /sourcing/calculation/read-selection',
    'POST /sourcing/calculation/summary',
    'POST /sourcing/calculation/compare',
    'POST /solutions/custom-part/bulk',
];

const offTheShelfOfferAdditionalEndpoints = {
    'GET /offers/off-the-shelf/by-ipn': endpoint({
        description: 'Fetches all the offers for the given IPN',
        pathParams: z.undefined(),
        queryParams: z.union([
            z.object({
                ipn: z.string(),
                source: z.union([z.literal('Inventory'), z.literal('Supplier')]),
                rfq_context: z.literal('WithinRfQ'),
                rfq_id: z.string(),
            }),
            z.object({
                ipn: z.string(),
                source: z.union([z.literal('Inventory'), z.literal('Supplier')]),
                rfq_context: z.literal('OutsideRfQ'),
            }),
        ]),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(StandardPartOfferResponseRuntype),
        }),
    }),

    'POST /offer-update': endpoint({
        description: 'Pushes a single offer update task on the top of the queue',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            api: AllOriginRuntype,
            sourceable_part: z.string(),
        }),
        responseBody: z.object({ status: PartOfferUpdateStatusRuntype }),
    }),
};

const offTheShelfOfferSolutionsResponseRuntype: z.ZodType<{
    data: Array<StandardPartOfferWithSolutionsDTO>;
}> = z.object({
    data: z.array(StandardPartOfferWithSolutionsDTORuntype),
});

const offTheShelfOfferSolutionsEndpoints = {
    'POST /offers/off-the-shelf/with-solutions': endpoint({
        description: 'Fetches all the offers with the fastest and best-price solutions for the given OTS part',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.union([
            z.object({
                part: z.string(),
                quantities: z.array(z.number()),
                rfq_context: z.literal('WithinRfQ'),
                rfq_id: z.string(),
            }),
            z.object({
                part: z.string(),
                quantities: z.array(z.number()),
                rfq_context: z.literal('OutsideRfQ'),
            }),
        ]),
        responseBody: offTheShelfOfferSolutionsResponseRuntype,
    }),
};

// Group 1E: Combined offer endpoints
const offersEndpoints = {
    'POST /offers/find': endpoint({
        description: 'Fetches all the offers for the given list of parts',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ parts: z.array(PartDTORuntype) }),
        responseBody: z.object({
            data: z.array(
                z.union([
                    z.object({
                        requested_part: StandardPartDTORuntype,
                        offers: z.array(StandardPartOfferResponseRuntype),
                    }),
                    z.object({
                        requested_part: CustomOptionPartDTORuntype,
                        offers: z.array(CustomOptionOfferResponseRuntype),
                    }),
                ]),
            ),
        }),
    }),

    'POST /offers/bulk': endpoint({
        description: 'Returns a list of offers given their IDs for both custom and standard bulk offers',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ offers: z.array(OfferRefRuntype) }),
        responseBody: z.object({
            data: z.array(OfferResponseRuntype),
        }),
    }),

    'POST /offers/v1/find': endpoint({
        description:
            'Finds offers by linked parts or by offer import contexts, cursor-paginated. ' +
            'This is the private route behind the public POST /offers/find.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FindOffersRequestRuntype,
        responseBody: FindOffersResultRuntype,
    }),
};

const pdfAnalysisEndpoints = {
    'POST /analyze/pdf': endpoint({
        description: 'Extracts key-value pairs, named entities and other values from a PDF',
        pathParams: z.undefined(),
        queryParams: z.object({
            type: z.union([z.literal('Offer'), z.literal('General')]),
        }),
        requestBody: z.instanceof(FormData),
        responseBody: PdfAnalyzeResponseRuntype,
    }),
};

const importEndpoints = {
    'POST /offers/import': endpoint({
        description: 'Imports offers',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.array(ImportOffersRequestBodyRuntype),
        responseBody: z.array(
            z.object({
                status: z.number(),
                error_code: z.string().nullable(),
                part: z
                    .object({
                        internal_part_number: z.string().optional(),
                        manufacturer_part_number: z.string().optional(),
                        manufacturer: z.string().optional(),
                    })
                    .nullable()
                    .optional(),
                description: z.string().optional(),
                customer: z
                    .object({
                        name: z.string().optional(),
                        number: z.string(),
                    })
                    .nullable()
                    .optional(),
            }),
        ),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
};

const offTheShelfOfferCrudEndpoints = {
    'PATCH /offers/off-the-shelf/:id': endpoint({
        description: 'Updates an offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: StandardPartOfferUpdateDTORuntype,
        responseBody: z.object({
            data: StandardPartOfferResponseRuntype,
        }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
        ],
    }),

    'POST /offers/off-the-shelf': endpoint({
        description: 'Creates an off-the-shelf offer',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: StandardPartOfferInputDTORuntype,
        responseBody: z.object({
            data: StandardPartOfferResponseRuntype,
        }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
};

const customPartOfferReadEndpoints = {
    'GET /offers/custom-part/:id': endpoint({
        description: 'Fetches a custom offer by id',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: CustomOptionOfferResponseRuntype }),
    }),

    'GET /offers/custom-part': endpoint({
        description: 'Fetches all offers for a given custom part',
        pathParams: z.undefined(),
        queryParams: z.union([
            z.object({
                part: z.string(),
                rfq_context: z.literal('WithinRfQ'),
                rfq_id: z.string(),
            }),
            z.object({
                part: z.string(),
                rfq_context: z.literal('OutsideRfQ'),
            }),
        ]),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(CustomOptionOfferResponseRuntype) }),
    }),
};

const customPartOfferFileEndpoints = {
    'POST /offers/custom-part/:id/additional-files/duplicate': endpoint({
        description: 'Duplicates an additional file for a custom part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            file_names: z.array(z.string()),
            new_offer_id: z.string(),
        }),
        responseBody: z.string(),
    }),

    'GET /offers/custom-part/:id/additional-files/upload-link': endpoint({
        description: 'Returns a link to upload additional files to a custom part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                url: z.string(),
            }),
        }),
    }),

    'GET /offers/custom-part/:id/additional-files': endpoint({
        description: 'Returns a list of additional files for a custom part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ items: z.array(z.string()) }),
    }),

    'DELETE /offers/custom-part/:id/additional-files': endpoint({
        description: 'Deletes an additional file for a custom part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ file_name: z.string() }),
        responseBody: z.string(),
        invalidates: ['GET /offers/custom-part/:id/additional-files'],
    }),

    'GET /offers/custom-part/:id/download-order-files': endpoint({
        description: 'Downloads the order files of a custom part offer as a zip',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return {
                fileName: getNormalizedFileName(response),
                blob: await response.blob(),
            };
        },
    }),
};

const customPartOfferBulkEndpoints = {
    'POST /offers/custom-part/bulk': endpoint({
        description: 'Returns a list of offers given their IDs',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ ids: z.array(z.string()) }),
        responseBody: z.object({
            items: z.array(CustomOptionOfferResponseRuntype),
        }),
    }),
};

const customPartOfferCreateEndpoints = {
    'POST /offers/custom-part': endpoint({
        description: 'Creates a custom-part offer',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CustomPartOfferInputDTORuntype,
        responseBody: z.object({ data: CustomOptionOfferResponseRuntype }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),

    'POST /offers/custom-part/bulk/import': endpoint({
        description: 'Creates multiple custom-part offers in bulk',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: BulkCustomPartOfferRequestDTORuntype,
        responseBody: z.object({
            items: z.array(CustomOptionOfferResponseRuntype),
            quote_tracking_id: z.string().nullable(),
        }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
};

const customPartOfferUpdateEndpoints = {
    'PATCH /offers/custom-part/:id': endpoint({
        description: 'Updates a custom-part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: CustomPartOfferUpdateDTORuntype,
        responseBody: z.object({ data: CustomOptionOfferResponseRuntype }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),

    'PATCH /offers/custom-part/:id/copy-panel-from-design': endpoint({
        description: 'Copies panel from design to custom part offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.object({ assembly_id: AssemblyIdRuntype }),
        requestBody: z.undefined(),
        responseBody: z.null(),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
};

const customPartOfferWithSolutionsEndpoints = {
    'POST /offers/custom-part/with-solutions': endpoint({
        description: 'Fetches all offers for a given custom part with solutions',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.union([
            z.object({
                part: z.string(),
                quantities: z.array(QuantityRuntype),
                rfq_context: z.literal('WithinRfQ'),
                rfq_id: z.string(),
            }),
            z.object({
                part: z.string(),
                quantities: z.array(QuantityRuntype),
                rfq_context: z.literal('OutsideRfQ'),
            }),
        ]),
        responseBody: z.object({
            data: z.array(z.custom<CustomPartOfferWithSolutionsDTO>()),
        }),
    }),
};

// Group 5B2: Generic single and bulk offer deletion endpoints
const offerDeletionEndpoints = {
    'DELETE /offers/:id': endpoint({
        description:
            'Deletes a single offer of any kind by its offer ID (an opaque string, as returned in offer responses). ' +
            'Responds 409 offer.cannot_delete_public_off_the_shelf_offers if the offer ID references a public offer. ' +
            'Responds 409 offer.cannot_delete_referenced_offers if the offer is referenced by a sourcing solution ' +
            'selection, unless force_delete=true, which unlinks it and resets the affected line items to pending.',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.object({ force_delete: z.boolean().optional() }),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
        removes: [
            'GET /offers/custom-part',
            'POST /offers/custom-part/bulk',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
    'POST /offers/delete': endpoint({
        description:
            'Deletes offers of any kind in bulk by their offer IDs (opaque strings, as returned in offer responses; ' +
            'at most 100,000 per request). Returns the set of IDs that were actually deleted; nonexistent IDs are ' +
            'ignored, which makes the operation idempotent. Responds 409 with public_off_the_shelf_offer_ids if any ' +
            'ID references a public offer. Responds 409 with referenced_offer_ids if any offer is referenced by a ' +
            'sourcing solution selection, unless force_delete=true, which unlinks them and resets the affected line ' +
            'items to pending. On conflict, no offers are deleted.',
        pathParams: z.undefined(),
        queryParams: z.object({ force_delete: z.boolean().optional() }),
        requestBody: z.object({ ids: z.array(z.string()) }),
        responseBody: z.object({ deleted_ids: z.array(z.string()) }),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
        removes: [
            'GET /offers/custom-part',
            'POST /offers/custom-part/bulk',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',
        ],
    }),
};

const pcbAndDeleteEndpoints = {
    'POST /offers/pcb/:id/update': endpoint({
        description: 'Updates offers for a pcb',
        pathParams: z.object({ id: PcbIdRuntype }),
        queryParams: z.object({ assembly_id: AssemblyIdRuntype.optional() }),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: [
            ...generalOfferEndpointsToInvalidate,
            'POST /assemblies/:id/pcb/:pcbId/offer-state',
            'POST /offers/custom-part/bulk',
            'GET /offers/custom-part/:id',
            'GET /offers/custom-part',
            'GET /sourcing-line-item/:id/solutions',
            'POST /offers/off-the-shelf/with-solutions',
            'POST /sourcing-scenarios/bulk',

            'GET /panels',
        ],
    }),
};

const offTheShelfAttachmentEndpoints = {
    'GET /offers/off-the-shelf/:id/additional-files/upload-link': endpoint({
        description: 'Returns a link to upload additional files to a off the shelf offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                url: z.string(),
            }),
        }),
    }),
};

const offTheShelfAffiliateUrlEndpoints = {
    'GET /offers/off-the-shelf/:id/affiliate-url': endpoint({
        description: 'Fetches the affiliate URL for the given offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ affiliate_url: z.string().nullable() }),
    }),
};

const offTheShelfFileManagementEndpoints = {
    'POST /offers/off-the-shelf/:id/additional-files/duplicate': endpoint({
        description: 'Duplicates an additional file for a off the shelf offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            file_names: z.array(z.string()),
            new_offer_id: z.string(),
        }),
        responseBody: z.string(),
    }),

    'GET /offers/off-the-shelf/:id/additional-files': endpoint({
        description: 'Returns a list of additional files for a off the shelf offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ items: z.array(z.string()) }),
    }),

    'DELETE /offers/off-the-shelf/:id/additional-files': endpoint({
        description: 'Deletes an additional file for a off the shelf offer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ file_name: z.string() }),
        responseBody: z.string(),
        invalidates: ['GET /offers/off-the-shelf/:id/additional-files'],
    }),
};

type OffTheShelfOfferAdditionalEndpoints = typeof offTheShelfOfferAdditionalEndpoints;
type OffTheShelfOfferSolutionsEndpoints = typeof offTheShelfOfferSolutionsEndpoints;
type OffersEndpoints = typeof offersEndpoints;
type PdfAnalysisEndpoints = typeof pdfAnalysisEndpoints;
type ImportEndpoints = typeof importEndpoints;
type OffTheShelfOfferCrudEndpoints = typeof offTheShelfOfferCrudEndpoints;
type CustomPartOfferReadEndpoints = typeof customPartOfferReadEndpoints;
type CustomPartOfferWithSolutionsEndpoints = typeof customPartOfferWithSolutionsEndpoints;
type CustomPartOfferFileEndpoints = typeof customPartOfferFileEndpoints;
type CustomPartOfferBulkEndpoints = typeof customPartOfferBulkEndpoints;
type CustomPartOfferCreateEndpoints = typeof customPartOfferCreateEndpoints;
type CustomPartOfferUpdateEndpoints = typeof customPartOfferUpdateEndpoints;
type OfferDeletionEndpoints = typeof offerDeletionEndpoints;
type PcbAndDeleteEndpoints = typeof pcbAndDeleteEndpoints;
type OffTheShelfAttachmentEndpoints = typeof offTheShelfAttachmentEndpoints;
type OffTheShelfAffiliateUrlEndpoints = typeof offTheShelfAffiliateUrlEndpoints;
type OffTheShelfFileManagementEndpoints = typeof offTheShelfFileManagementEndpoints;

interface OfferEndpoints
    extends
        OffTheShelfOfferAdditionalEndpoints,
        OffTheShelfOfferSolutionsEndpoints,
        OffersEndpoints,
        PdfAnalysisEndpoints,
        ImportEndpoints,
        OffTheShelfOfferCrudEndpoints,
        CustomPartOfferReadEndpoints,
        CustomPartOfferWithSolutionsEndpoints,
        CustomPartOfferFileEndpoints,
        CustomPartOfferBulkEndpoints,
        CustomPartOfferCreateEndpoints,
        CustomPartOfferUpdateEndpoints,
        OfferDeletionEndpoints,
        PcbAndDeleteEndpoints,
        OffTheShelfAttachmentEndpoints,
        OffTheShelfAffiliateUrlEndpoints,
        OffTheShelfFileManagementEndpoints {}

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.
export const offerEndpoints: OfferEndpoints = {
    ...offTheShelfOfferAdditionalEndpoints,
    ...offTheShelfOfferSolutionsEndpoints,
    ...offersEndpoints,
    ...pdfAnalysisEndpoints,
    ...importEndpoints,
    ...offTheShelfOfferCrudEndpoints,
    ...customPartOfferReadEndpoints,
    ...customPartOfferWithSolutionsEndpoints,
    ...customPartOfferFileEndpoints,
    ...customPartOfferBulkEndpoints,
    ...customPartOfferCreateEndpoints,
    ...customPartOfferUpdateEndpoints,
    ...offerDeletionEndpoints,
    ...pcbAndDeleteEndpoints,
    ...offTheShelfAttachmentEndpoints,
    ...offTheShelfAffiliateUrlEndpoints,
    ...offTheShelfFileManagementEndpoints,
};
